import React, { useContext, useState } from 'react'
import { Alert, FormControlLabel, Switch, ToggleButton, ToggleButtonGroup } from '@mui/material'
import TestList from '../../../topBar/TestList'
import { SessionContext } from '../../../../context/SessionContext'
import { getSessionById, updateSessionFields } from '../../../../service/sessionService'
import { isClassStyle } from '../../testResults'
import { forgetArrivalDraft } from './CodeStubStep'

// Exercise types that can be a class exercise (session.test_code.test_style 'class').
const CLASS_STYLE_TYPES = ['Python', 'Vizmental']

// The starter code the AI drafts on Code Stubs: one def or class line and `pass`. Cleared
// when the style changes, so that step drafts the other kind.
const DRAFTED_STUB = /^\s*(def|class)\s+\w+[^\n]*:\s*\n(\s*#[^\n]*\n)*\s*pass\s*$/

// Step 3 (code types). Renders the existing TestList panel unmodified.
//
// TestList is zero-prop and reads everything from context, so the wizard supplies the
// contexts instead of the /session/:id page: SessionContext comes from the wizard's own
// provider, ModeContext + SelectedCodeContext from DashboardProviders on the route, and
// TestResultContext from CreateQuestionPage (it is mounted at Home.jsx:163, i.e. only
// inside /session/:id, and TestList destructures it with no default).
//
// Its editor branch is gated on `currentUser.role < 3 && Mode == false` (TestList.jsx:382).
// Mode defaults to false in ModeContextProvider and only instructors (role <= 1) reach
// the wizard, so both hold here.
//
// TestList's own "Create Unit Tests" button is hidden here: compiling the harness is what
// Next does, so the instructor cannot reach the Code Stubs step having skipped it. The
// ref hands the wizard that same handler.
//
// This step is where the instructor decides whether the exercise has test cases at all.
// Off hides the editor (nothing to configure) and Next skips the compile; the wizard
// stores the answer as session.enable_tests when leaving the step.
//
// A Python exercise can also be about a class: the tests are then checks that use the
// student's class (TestList shows ClassCheckList for them). The choice is saved at once as
// test_code.test_style and clears the tests, which are written for one style or the other.
// It cannot change while tests are being generated or compiled (`busy`: the wizard's
// Continue): those finish by saving tests of the old style. `onStyleChange` tells the
// wizard that its record of the last compile no longer applies.
const TestCasesStep = ({ typeMeta, createUnitTestsRef, testsOn, setTestsOn, onTestListChange, busy, onStyleChange }) => {
  const { session, setSession } = useContext(SessionContext)
  const style = isClassStyle(session) ? 'class' : 'function'
  const canBeClass = CLASS_STYLE_TYPES.includes(session?.type)
  const [switching, setSwitching] = useState(false)
  const [styleError, setStyleError] = useState('')

  const chooseStyle = async (next) => {
    if (!next || next === style || switching || !session?.id) return
    const editor = createUnitTestsRef?.current
    if (busy || editor?.loading || editor?.generating) {
      setStyleError('Wait until the tests finish generating or compiling, then switch.')
      return
    }
    const listed = editor?.testList ?? session.test_list ?? []
    if (
      listed.length &&
      !window.confirm(
        `Switch to ${next === 'class' ? 'a class exercise' : 'a function exercise'}? The ${listed.length} test${
          listed.length === 1 ? '' : 's'
        } you have now will be removed.`
      )
    )
      return
    setSwitching(true)
    setStyleError('')
    try {
      // The saved template decides what is kept, so never decide without it.
      const fresh = await getSessionById(session.id)
      if (!fresh) throw new Error('The session could not be read')
      const saved = fresh.test_code || {}
      const template = typeof saved.template === 'string' && !DRAFTED_STUB.test(saved.template) ? saved.template : ''
      const testCode = { ...saved, starter: '', unit_test: '', template, test_style: next }
      // One request: the style and the tests change together or not at all.
      if (!(await updateSessionFields(session.id, { test_code: testCode, test_list: [] }))) {
        setStyleError('Could not save the change. Check your connection and try again.')
        return
      }
      if (!template) forgetArrivalDraft(session.id)
      setSession({ ...fresh, test_code: testCode, test_list: [] })
      onStyleChange?.()
    } catch (error) {
      console.error('Switching the exercise style failed:', error)
      setStyleError('Could not save the change. Check your connection and try again.')
    } finally {
      setSwitching(false)
    }
  }

  return (
    <>
      <div className="cq-card__head">
        <div>
          <h2>Testcases</h2>
          <p>
            Generate cases with AI, edit them, then compile them into a{' '}
            {typeMeta?.testLanguage || 'Python'} harness.
          </p>
        </div>
      </div>

      <div className="cq-field" style={{ marginBottom: 16 }}>
        <FormControlLabel
          control={
            <Switch
              checked={testsOn}
              onChange={(e) => setTestsOn(e.target.checked)}
              inputProps={{ 'aria-describedby': 'cq-tests-toggle-help' }}
            />
          }
          label="Grade with test cases"
        />
        <div id="cq-tests-toggle-help" className="cq-saved-hint" style={{ marginLeft: 48 }}>
          {testsOn
            ? 'Students see a Test Cases list, and Run checks their code against it.'
            : 'Students see no Test Cases list, and Run just executes their code. Nothing to set up here - press Continue.'}
        </div>
      </div>

      {/* Hidden, not unmounted, while tests are off: TestList holds the list being
          edited in local state and never calls setSession, so remounting it would re-seed
          from the wizard's stale copy and the next edit would overwrite saved cases. */}
      <div style={{ display: testsOn ? undefined : 'none' }}>
      {canBeClass && (
        <div className="cq-field" style={{ marginBottom: 16 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={style}
            disabled={switching || busy}
            onChange={(_e, next) => chooseStyle(next)}
            aria-label="What students write"
            aria-describedby="cq-test-style-help">
            <ToggleButton value="function">A function</ToggleButton>
            <ToggleButton value="class">A class</ToggleButton>
          </ToggleButtonGroup>
          <div id="cq-test-style-help" className="cq-saved-hint" style={{ marginTop: 6 }}>
            {style === 'class'
              ? 'Each test is a check: a few lines that use the class through the methods your description names, ending in an expression that must be True.'
              : 'Each test calls the function with an input and compares what it returns with the expected output.'}
          </div>
          {styleError && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setStyleError('')}>
              {styleError}
            </Alert>
          )}
        </div>
      )}
      <Alert severity="info" sx={{ mb: 2 }}>
        {style === 'class' ? (
          <>
            Draft checks from your description or write them by hand. On <strong>Continue</strong>,
            each check is run against an AI reference class, which takes a few seconds.
          </>
        ) : (
          <>
            Generate cases from your description or add them by hand. On <strong>Continue</strong>,
            each case is checked against an AI reference solution, which takes a few seconds.
          </>
        )}
      </Alert>

      {/* Remount when the session identity changes so TestList re-seeds its local testList
          from the freshly fetched row rather than its stale first sync (TestList only
          re-syncs editors on a genuine session change), and when the style changes. */}
      <TestList
        key={`${session?.id}:${style}`}
        compact
        hideCreateButton
        createUnitTestsRef={createUnitTestsRef}
        onTestListChange={onTestListChange}
      />
      </div>
    </>
  )
}

export default TestCasesStep
